import { useRoutingEmergencyOverride } from '../../api/useRoutingDr.ts';
import { useT } from '../../i18n/index.ts';

/**
 * Warns, on the surfaces that move the *global* routing config, that doing so
 * changes nothing while the emergency override is engaged (#1422).
 *
 * Tier 0 sits above the global assignment, so a switch or a rollback performed
 * during an engaged override reports success, converges its cache, restores the
 * config in the library — and leaves every tenant exactly where it was. Without
 * this notice the console shows an operator three separate confirmations of a
 * change that did not happen.
 *
 * Keyed on `effective`, not `engaged`: an override past its expiry, or pointing
 * at an archived config, is no longer above anything, and a warning shown then
 * would train operators to ignore it.
 */
export function OverrideInForceNotice(): JSX.Element | null {
  const t = useT();
  const query = useRoutingEmergencyOverride();
  const override = query.data?.override ?? null;
  if (override === null || !override.effective) return null;

  return (
    <p
      role="alert"
      className="m-0 rounded-md border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense leading-5 text-bad-bright"
      data-testid="routing-dr-override-in-force-notice"
    >
      {t('routing_dr.override.in_force_notice', {
        config: override.config_name ?? override.config_id ?? '',
      })}
    </p>
  );
}
